import {
  SeasonInfo,
  VideoItemData,
  MarkLabelInfo,
  Poster,
  NextPageInfo,
  ExperimentInfo,
  WatchList,
  HistoryRecord,
  PlayListUiType,
  TagLabel,
  VideoType,
} from './common';
import { PosterLabel } from './history';

// 接口返回数据
export interface VideoDetailBasicInfo {
  vid: string; // 启播的vid，有可能跟req传的不同
  cid: string; // 上面vid对应的cid
  video_type: number; // 视频类型，1是专辑，2是短视频
  primary_category: number; // 视频一级分类类型
  area_id: number; // 制片地区（和国家码数值定义一致）
  season_list: SeasonInfo[];
  current_video_data: VideoItemData;
  full_episode_count: number; // 视频一共有多少集
  update_episode: number; // 视频更新到第几集
  update_time: string; // 视频最新一集的更新时间（秒）
  online_time: string; // 上线时间 (时间戳)
  album_vertical_img_url: string; // 专辑竖图
  mark_label_info_list: MarkLabelInfo[]; // 待替换角标信息列表
  full_episode_count_text: string; // 视频一共有多少集文案
  show_credit_entry: boolean; // 是否展示积分入口
  show_score_entry: boolean; // 是否展示剧集分数入口
  content_score: number; // 剧集分数。如果小于0，则表示未开分
  poster: Poster; // cid维度海报、标题信息
  auto_play_video_data: VideoItemData; // 详情页自动播vid
}

export interface RecommendListInfo {
  next_page_info: NextPageInfo;
  recommend_list: Poster[];
}

export interface PlayListInfo {
  next_page_info: NextPageInfo;
  play_list_info: VideoItemData[];
}

export interface PlayUrlResponse {
  ret?: number;
  code?: number;
  data?: {
    video_data: VideoDetailBasicInfo;
    experiment_info: ExperimentInfo[]; // A/B test信息
    watch_list: WatchList; // 看单
    recommend_list: RecommendListInfo; // 推荐视频
    play_list_info: PlayListInfo; // 播放列表
    related_video_info: PlayListInfo; // 花絮列表
  };
}

export interface PlayListResponse {
  ret?: number;
  code?: number;
  data?: {
    video_list: VideoItemData[]; // 视频列表
    next_page_info: NextPageInfo; // 下一页信息
  };
}

export interface PlayHistoryResponse {
  ret?: number;
  code?: number;
  data?: {
    record: HistoryRecord; // 历史记录
  };
}

// playDetail页面中使用的一些枚举值
export enum FocusKey {
  PlayDetailPage = 'play-detail-page',
  PlayDetailErrorPageRetryBtn = 'play-detail-error-page-retry',
  PlayDetailSeriesPageSeasonList = 'play-detail-series-page-season-list',
  PlayDetailSeriesPageGrouplist = 'play-detail-series-page-group-list',
  PlayDetailSeriesPageVideoList = 'play-detail-series-page-video-list',
  PlayDetailMainPageMoreBtn = 'play-detail-main-page-more-btn',
  PlayDetailMainPageOptionList = 'play-detail-main-page-option-list',
  PlayDetailMainPageRecommendList = 'play-detail-main-page-recommend-list',
}

export enum SeasonType {
  SeasonNormal = 'season_normal',
  SeasonRelatedvideo = 'season_relatedvideo',
}

export enum PlayPageFocusModule {
  MoreBtn = 'moreBtn',
  OptionList = 'optionList',
  RecommendList = 'recommendList',
  SeasonList = 'seasonList',
  GroupList = 'groupList',
  VideoList = 'videoList',
  RetryBtn = 'retryBtn',
  DescriptionDetail = 'description_detail',
}

export enum PlayListUiStyle {
  ThirdColumns = 'third_columns',
  SecondColumns = 'second_columns',
}

export type PlayListStyleType = {
  [key in PlayListUiType]: 'third_columns' | 'second_columns';
};

export const DEFAULT_SEASON_IDX = 0;
export const playListStyleType: PlayListStyleType = {
  [PlayListUiType.PlayListTypeText]: PlayListUiStyle.ThirdColumns,
  [PlayListUiType.PlayListTypePoster]: PlayListUiStyle.SecondColumns,
  [PlayListUiType.PlayListTypeVerticalPoster]: PlayListUiStyle.SecondColumns,
};
export type PlayListStyleValue = (typeof playListStyleType)[keyof typeof playListStyleType];

// 数据处理后的结构
export interface AutoPlayVideoData {
  cid?: string;
  vid?: string;
  vidParams?: string;
  videoPlayTime: number;
  title: string;
  isHistory: boolean;
}

export interface HandledVideoItemData {
  cid?: string;
  vid?: string;
  pid?: string;
  videoType: VideoType;
  episodeId?: number;
  cover: {
    horizontal: string;
    vertical: string;
  };
  fallbackCover: {
    horizontal: string;
    vertical: string;
  };
  cornerLabels: PosterLabel[];
  isTrailer: boolean;
  payPreview: boolean;
  title: string;
  duration: string;
  videoPlayTime?: number;
  videoTotalTime: number;
  skipHeadBegin?: number;
}

export interface HandledCoverItemData {
  cid?: string;
  vid?: string;
  pid?: string;
  artTitleImg: string;
  mainTitle: string;
  cover: {
    horizontal: string;
    vertical: string;
  };
  fallbackCover: {
    horizontal: string;
    vertical: string;
  };
  cornerLabels: PosterLabel[];
  reportParams: string;
}

export interface InitialRequestParams {
  nextPageInfo: NextPageInfo;
  initialVal: HandledVideoItemData[];
}

export interface GroupListItem {
  title: string;
  videoList: HandledVideoItemData[];
}

export interface HandledSeasonItem {
  cid: string;
  seasonType: SeasonType; // 正片还是花絮
  playListStyleKey: PlayListStyleValue; // 播放列表样式的 key值
  groupList: GroupListItem[];
  defGroupIdx: number;
  defPosterIdx: number;
  isLoaded: boolean;
  isLoading: boolean;
  isError: boolean;
  artTitleImg: string;
  title: string;
  pureImgOpUrl: string;
  tagLabelList: TagLabel[];
  initialRequestParams: InitialRequestParams;

  // detailInfo
  detailInfo: string;
}

export enum OptionType {
  PlayBtn = 'playBtn',
  PlayListBtn = 'playListBtn',
}

export const OptionTypeToModName = {
  [OptionType.PlayBtn]: 'play',
  [OptionType.PlayListBtn]: 'episode',
};

export interface OptionListItem {
  type: OptionType;
  text: string;
  // action: () => void;
}

export interface NextVidInfoItem {
  [key: string]: {
    nextVid: string;
    type: VideoType;
  };
}

export interface NextVidInfo {
  [key: string]: NextVidInfoItem;
}

export type OptionalHandledSeasonItem = Partial<HandledSeasonItem>;

// store中的状态值
export interface PlayDetailState {
  pureImgOpUrl: string;
  artTitleImg: string;
  mainTitle: string;
  tagLabelList: TagLabel[];
  description: string;
  updateInfo: string;
  optionList: OptionListItem[];
  recommendList: HandledCoverItemData[];

  curShowSeason: HandledSeasonItem[];
  defSeasonIdx: number;

  autoPlayVideoInfo: AutoPlayVideoData;
  isShowDetailDescription: boolean;
  isShowSeasonListPage: boolean;
  isDescriptOverFlow: boolean;

  isLoading: boolean;
  isLoaded: boolean;
  isError: boolean;

  selectSeasonIdx: number;
  selectGroupIdx: number;
  selectPosterIdx: number;

  selectModule: string;

  timer: number | null;
  autoEnterPlayerPage: boolean;
  nextVidData: NextVidInfo;
}

export interface PlayDetailActions {
  init: (cid: string, vid: string, isVip: boolean) => Promise<void>;
  reset: () => void;
  resetLoadStatus: () => void;
  requestSeasonVideoList: (requestParams: InitialRequestParams) => Promise<HandledVideoItemData[]>;
  setSeasonIdx: (idx: number, autoPlayVideoData?: AutoPlayVideoData) => Promise<void>;
  updateCurShowSeason: (idx: number, curSeason: OptionalHandledSeasonItem) => void;
  updateNextVidData: (idx: number, nextVidInfo: NextVidInfoItem) => void;
  autoEnterPlayerPageTimer: (refreshTime: boolean) => void;
  clearAutoEnterPlayerPageTimer: () => void;
  getNextVid: (cid: string, curVid: string) => string;
  getVidType: (cid: string, curVid: string) => VideoType;
  keyToVal: <K extends keyof PlayDetailStore>(key: K) => PlayDetailStore[K];
  setKeyVal: <K extends keyof PlayDetailStore>(key: K, val: PlayDetailStore[K]) => void;
}

export type PlayDetailStore = PlayDetailState & PlayDetailActions;

export enum PlayPagePlayerState {
  Play = 'play',
  Pause = 'pause',
  Stop = 'stop',
}
